import * as React from "react"
import * as LabelPrimitive from "@radix-ui/react-label"
import { cn } from "@/lib/utils"
import { FieldHelp } from "@/components/ui/field-help"

type LabelProps = React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root> & {
  helpText?: React.ReactNode
  requiredIndicator?: boolean
  hideHelp?: boolean
}

function hasRequiredMarker(node: React.ReactNode): boolean {
  if (typeof node === "string") return /\*\s*$/.test(node)
  if (Array.isArray(node)) return node.some(hasRequiredMarker)
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
    return hasRequiredMarker(node.props.children)
  }
  return false
}

function stripRequiredMarker(node: React.ReactNode): React.ReactNode {
  if (typeof node === "string") return node.replace(/\s*\*\s*$/, "")
  if (Array.isArray(node)) return React.Children.map(node, stripRequiredMarker)
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
    return React.cloneElement(node, undefined, stripRequiredMarker(node.props.children))
  }
  return node
}

function labelText(node: React.ReactNode): string {
  if (typeof node === "string" || typeof node === "number") return String(node)
  if (Array.isArray(node)) return node.map(labelText).join("")
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) return labelText(node.props.children)
  return ""
}

const Label = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  LabelProps
>(({ className, children, helpText, requiredIndicator, hideHelp = false, ...props }, ref) => {
  const required = requiredIndicator ?? hasRequiredMarker(children)
  const cleanChildren = stripRequiredMarker(children)
  const field = labelText(cleanChildren).trim()

  return (
    <LabelPrimitive.Root
      ref={ref}
      className={cn("inline-flex items-center gap-1.5", className)}
      {...props}
    >
      {cleanChildren}
      {required && <span className="font-semibold text-destructive" aria-hidden="true">*</span>}
      {!hideHelp && field && helpText ? <FieldHelp field={field} helpText={helpText} /> : null}
    </LabelPrimitive.Root>
  )
})
Label.displayName = LabelPrimitive.Root.displayName

export { Label }
